import { useContext, useEffect, useMemo, useState } from 'react'

import { useAnalyzeGscKeywordsMutation } from '@/common/api/modules/gsc'
import { GscKeywordPerformance } from '@/common/api/params/gsc'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import ListHeaderSortButton from '@/components/view/atoms/icon/ListHeaderSortButton'
import LoadingObject, { LoadingObjectType } from '@/components/view/atoms/loading/LoadingObject'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Line from '@/components/view/layouts/area/Line'
import Modal from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { splitKeywords } from '@/components/container/form/modules/media/articleCreator/keywordAnalysisSchema'

import { GscKeywordAnalysisModalContext } from './GscKeywordAnalysisModalProvider'

interface ContentProps {
  siteId: number
  keyword: string
  siteUrl?: string
  days?: number
  onApply(keyword: string): void
  onClose(): void
}

type SortKey = 'keyword' | 'clicks' | 'impressions' | 'ctr' | 'position'
type SortOrder = 'asc' | 'desc'

const HEADERS: { key: SortKey; label: string }[] = [
  { key: 'keyword', label: 'キーワード' },
  { key: 'clicks', label: 'クリック数' },
  { key: 'impressions', label: '表示回数' },
  { key: 'ctr', label: 'CTR' },
  { key: 'position', label: '掲載順位' }
]

// モーダルが開くたびに再マウントし、分析実行・選択状態を初期化する
const GscKeywordAnalysisModalContent = ({
  siteId,
  keyword,
  siteUrl,
  days,
  onApply,
  onClose
}: ContentProps) => {
  const [selected, setSelected] = useState<GscKeywordPerformance | null>(null)
  const [searchText, setSearchText] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('clicks')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  const { trigger: analyze, data, isMutating: analyzing } = useAnalyzeGscKeywordsMutation()

  useEffect(() => {
    analyze({
      site_id: siteId,
      keywords: splitKeywords(keyword),
      site_url: siteUrl,
      days
    }).catch(() => undefined)
    // マウント時に一度だけ分析を実行する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredKeywords = useMemo(() => {
    let kws = data?.keywords ?? []
    if (searchText) {
      kws = kws.filter((k) => k.keyword.includes(searchText))
    }
    return [...kws].sort((a, b) => {
      const av = a[sortKey] ?? 0
      const bv = b[sortKey] ?? 0
      return sortOrder === 'desc' ? (bv > av ? 1 : -1) : av > bv ? 1 : -1
    })
  }, [data?.keywords, searchText, sortKey, sortOrder])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortOrder('desc')
    }
  }

  const handleApply = () => {
    if (!selected) return
    onApply(selected.keyword)
    onClose()
  }

  return (
    <>
      <ModalContent type={ModalContentType.NO_FRAME}>
        {analyzing ? (
          <div className='flex flex-col items-center gap-3 p-8'>
            <LoadingObject type={LoadingObjectType.TEXT} />
            <Text size={14} color={TextColor.SECONDARY}>
              GSCの実績データを取得しています
            </Text>
          </div>
        ) : !data?.connected ? (
          <Text size={14} color={TextColor.CRITICAL}>
            {data?.message || 'GSC未連携です。先にGSC連携を設定してください。'}
          </Text>
        ) : (
          <>
            {(data.gsc_site_url || !data.filtered) && (
              <AreaContent>
                {data.gsc_site_url && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    分析対象サイト: {data.gsc_site_url}
                  </Text>
                )}
                {!data.filtered && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    キーワード未入力のため、サイト全体の検索クエリ実績を表示回数の多い順に表示しています
                  </Text>
                )}
              </AreaContent>
            )}
            {data.keywords.length > 0 ? (
              <div className='w-full'>
                <Line />
                <div className='flex flex-wrap items-center gap-2 p-3'>
                  <div className='flex-1' />
                  <TextField
                    type='search'
                    size={TextBoxSize.LARGE}
                    value={searchText}
                    placeholder='キーワードで検索する'
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <div className='ml-auto'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      {data.total_count > data.keywords.length
                        ? `全${data.total_count}件のうち表示回数上位${data.keywords.length}件`
                        : `全${data.keywords.length}件`}
                    </Text>
                  </div>
                </div>
                <table className='w-full'>
                  <thead>
                    <TableRow>
                      {HEADERS.map((h) => (
                        <TableHeadItem key={h.key}>
                          <ListHeaderSortButton
                            order={sortKey === h.key ? sortOrder : undefined}
                            onChange={() => handleSort(h.key)}
                            text={h.label}
                          />
                        </TableHeadItem>
                      ))}
                    </TableRow>
                  </thead>
                  <tbody>
                    {filteredKeywords.map((k, i) => {
                      const isSelected = selected?.keyword === k.keyword
                      const background = isSelected ? 'info' : 'primary'
                      return (
                        <TableRow
                          key={i}
                          hoveredOn={!isSelected}
                          attributes={{
                            onClick: () => setSelected(k),
                            style: { cursor: 'pointer' }
                          }}
                        >
                          <TableRowItem background={background}>
                            <Badge badgeType={BadgeType.KEYWORD}>{k.keyword}</Badge>
                          </TableRowItem>
                          <TableRowItem background={background}>
                            <Text size={12}>{k.clicks.toLocaleString()}</Text>
                          </TableRowItem>
                          <TableRowItem background={background}>
                            <Text size={12}>{k.impressions.toLocaleString()}</Text>
                          </TableRowItem>
                          <TableRowItem background={background}>
                            <Text size={12}>{k.ctr}%</Text>
                          </TableRowItem>
                          <TableRowItem background={background}>
                            <Text size={12}>{k.position}</Text>
                          </TableRowItem>
                        </TableRow>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <AreaContent>
                <Text size={14} color={TextColor.SECONDARY} align='center'>
                  {data.message ||
                    (data.filtered
                      ? '入力キーワードに一致する検索実績が見つかりませんでした'
                      : '対象期間の検索実績が見つかりませんでした')}
                </Text>
              </AreaContent>
            )}
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={onClose}>
          閉じる
        </Button>
        <Button color={ButtonColor.PRIMARY} size='large' disabled={!selected} onClick={handleApply}>
          このキーワードを反映する
        </Button>
      </ModalFooter>
    </>
  )
}

// 記事作成画面のキーワード選定用モーダル（GSCの実績データ〔クリック数・表示回数・掲載順位〕のみを表示。
// AIのようなキーワード提案は行わず、既に表示実績のある検索クエリを入力キーワードで絞り込む）
const GscKeywordAnalysisModal = () => {
  const { modal, showing, hide } = useContext(GscKeywordAnalysisModalContext)

  return (
    <Modal show={showing}>
      <ModalHeader text='GSCキーワード実績分析' onClickClose={hide} />
      {modal.showing && (
        <GscKeywordAnalysisModalContent
          siteId={modal.siteId}
          keyword={modal.keyword}
          siteUrl={modal.siteUrl}
          days={modal.days}
          onApply={modal.onApply}
          onClose={hide}
        />
      )}
    </Modal>
  )
}

export default GscKeywordAnalysisModal
